// UI-Primitives des CRM.
//
// Regeln, die hier eingebaut sind (und nicht pro Screen neu entschieden werden):
//   * Eine Textgröße pro Ebene, eine Höhe pro Control-Reihe.
//   * Tiefe über Layering + 1px-Hairlines, nicht über dicke Rahmen.
//   * Zahlen immer `tabular-nums` — Beträge dürfen beim Tippen nicht springen.
//   * Touch-Ziele mind. 44px; im Fahrer-Modus mehr.

import {
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  useEffect,
  useState,
} from "react";
import { useZurueckTaste } from "@/lib/zurueck-taste";

export const cn = (...cls: (string | false | null | undefined)[]) =>
  cls.filter(Boolean).join(" ");

/** Cent → "6,40 €" — nie mit toFixed auf Float-Basis rechnen. */
export const euro = (cents: number) =>
  (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

/** Menge hübsch: 2.5 → "2,5" · 20 → "20" */
export const qtyFmt = (n: number) =>
  Number(n).toLocaleString("de-DE", { maximumFractionDigits: 3 });

// ------------------------------------------------------------------ Button

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "ghost" | "danger" | "subtle";
  size?: "sm" | "md" | "lg";
  loading?: boolean;
};

export function Button({
  variant = "subtle",
  size = "md",
  loading,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  // md ist 44px hoch — Touch-Mindestmaß, und passt exakt zur Höhe von <Input>,
  // damit Such-/Filterleisten nicht ausfransen.
  const sizes = {
    sm: "h-9 px-3 text-sm gap-1.5",
    md: "h-11 px-4 text-sm gap-2",
    lg: "h-14 px-6 text-base gap-2.5",
  };
  const variants = {
    primary:
      "bg-gruen-500 text-gruen-on font-semibold hover:brightness-110 active:brightness-95 disabled:bg-ink-700 disabled:text-ink-400 disabled:brightness-100",
    subtle:
      "bg-ink-800 text-ink-100 border border-ink-700 hover:bg-ink-700 hover:border-ink-600 disabled:text-ink-500",
    ghost: "text-ink-300 hover:bg-ink-800 hover:text-ink-100 disabled:text-ink-500",
    danger: "bg-danger-bg/10 text-danger-fg border border-danger-bg/30 hover:bg-danger-bg/20",
  };
  return (
    <button
      className={cn(
        "inline-flex items-center justify-center rounded-lg font-medium",
        "transition-colors duration-fast",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gruen-500/60",
        "disabled:cursor-not-allowed",
        sizes[size],
        variants[variant],
        className
      )}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? <Spinner /> : children}
    </button>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        "inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent",
        className
      )}
    />
  );
}

// ------------------------------------------------------------------- Input

export function Input({
  className,
  ...rest
}: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className={cn(
        "h-11 w-full rounded-lg bg-ink-900 px-3.5 text-base text-ink-100",
        "border border-ink-700 placeholder:text-ink-500",
        "transition-colors duration-fast",
        "focus:border-gruen-500/60 focus:outline-none focus:ring-2 focus:ring-gruen-500/20",
        className
      )}
      {...rest}
    />
  );
}

export function Field({
  label,
  hint,
  optional,
  children,
}: {
  label: string;
  hint?: string;
  optional?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 flex items-baseline gap-2">
        <span className="text-xs font-medium uppercase tracking-wider text-ink-400">
          {label}
        </span>
        {optional && <span className="text-xs text-ink-500">optional</span>}
      </span>
      {children}
      {hint && <span className="mt-1 block text-xs text-ink-500">{hint}</span>}
    </label>
  );
}

// -------------------------------------------------------------------- Card

export function Card({
  className,
  children,
  ...rest
}: { className?: string; children: ReactNode } & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("rounded-xl border border-ink-700/70 bg-ink-800/60", className)}
      {...rest}
    >
      {children}
    </div>
  );
}

// --------------------------------------------------------------- Segmented

/** Segmented Control — für 2–4 sich ausschließende Optionen. */
export function Segmented<T extends string | number>({
  value,
  onChange,
  options,
  className,
  size = "md",
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
  className?: string;
  size?: "md" | "lg";
}) {
  const h = size === "lg" ? "h-12 text-[15px]" : "h-10 text-sm";
  return (
    <div
      className={cn(
        "inline-flex w-full rounded-lg border border-ink-700 bg-ink-900 p-1",
        className
      )}
      role="tablist"
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={String(o.value)}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(o.value)}
            className={cn(
              "flex-1 rounded-md font-medium transition-colors duration-fast",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gruen-500/60",
              h,
              active
                ? "bg-ink-700 text-ink-50 shadow-sm"
                : "text-ink-400 hover:text-ink-200"
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// -------------------------------------------------------------------- Chip

/**
 * `size="tap"` (Default) ist 40px hoch — groß genug für den Daumen im Auto.
 * `size="tag"` (32px) nur für reine ANZEIGE, nicht zum Antippen.
 */
export function Chip({
  active,
  onClick,
  children,
  className,
  size = "tap",
}: {
  active?: boolean;
  onClick?: () => void;
  children: ReactNode;
  className?: string;
  size?: "tap" | "tag";
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-4 text-sm font-medium",
        "border transition-colors duration-fast",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gruen-500/60",
        size === "tap" ? "h-10" : "h-8 px-3.5",
        active
          ? "border-gruen-500/50 bg-gruen-500/15 text-gruen-400"
          : "border-ink-700 bg-ink-800/60 text-ink-300 hover:border-ink-600 hover:text-ink-100",
        className
      )}
    >
      {children}
    </button>
  );
}

// ----------------------------------------------------------------- Stepper

/** Mengen-Stepper mit fixer Schrittweite (z.B. 2,5 kg Kartoffeln). */
export function Stepper({
  value,
  onChange,
  step = 1,
  min = 0,
  max = 9999,
  suffix,
}: {
  value: number;
  onChange: (v: number) => void;
  step?: number;
  min?: number;
  max?: number;
  suffix?: string;
}) {
  const clamp = (v: number) => Math.min(max, Math.max(min, Math.round(v * 1000) / 1000));

  // Die Zahl in der Mitte ist antippbar: Der Fahrer will an der Haustür „5 Sack"
  // eintippen können, nicht fünfmal auf „+“. Solange das Feld den Fokus hat,
  // gehört das Getippte ihm (auch der leere Zwischenstand beim Löschen); beim
  // Verlassen wird es normalisiert und in die Grenzen gezogen.
  const [fokus, setFokus] = useState(false);
  const [roh, setRoh] = useState("");
  const anzeige = fokus ? roh : qtyFmt(value);

  const tippe = (s: string) => {
    setRoh(s);
    // Live übernehmen, damit die Summe darüber sofort mitrechnet — aber ein
    // leeres Feld nicht als 0 verbuchen, sonst springt die Zahl beim Löschen.
    if (s.trim() === "") return;
    const n = Number(s.replace(",", ".").replace(/[^\d.]/g, ""));
    if (Number.isFinite(n)) onChange(clamp(n));
  };

  const verlassen = () => {
    setFokus(false);
    const n = Number(roh.replace(",", "."));
    onChange(clamp(roh.trim() === "" || !Number.isFinite(n) ? min : n));
  };

  return (
    <div className="inline-flex h-12 items-stretch overflow-hidden rounded-lg border border-ink-700 bg-ink-900">
      <button
        onClick={() => onChange(clamp(value - step))}
        disabled={value <= min}
        className="w-12 text-lg text-ink-300 transition-colors duration-fast hover:bg-ink-800 hover:text-ink-50 disabled:text-ink-500"
        aria-label="weniger"
      >
        −
      </button>
      <div className="flex min-w-[5rem] items-center justify-center gap-1 border-x border-ink-700 px-1">
        <input
          // inputMode numerisch: Auf dem Handy erscheint der Ziffernblock, nicht
          // die Schreibtastatur. text-base = 16px ist Pflicht — darunter zoomt
          // iOS beim Antippen ins Feld hinein.
          inputMode="decimal"
          value={anzeige}
          onFocus={(e) => {
            setFokus(true);
            setRoh(value ? String(value) : "");
            e.currentTarget.select();
          }}
          onChange={(e) => tippe(e.target.value)}
          onBlur={verlassen}
          aria-label="Menge"
          className="w-14 bg-transparent text-center text-base font-semibold tabular-nums text-ink-50 outline-none"
        />
        {suffix && <span className="text-sm text-ink-400">{suffix}</span>}
      </div>
      <button
        onClick={() => onChange(clamp(value + step))}
        disabled={value >= max}
        className="w-12 text-lg text-ink-300 transition-colors duration-fast hover:bg-ink-800 hover:text-ink-50 disabled:text-ink-500"
        aria-label="mehr"
      >
        +
      </button>
    </div>
  );
}

// ------------------------------------------------------------------- Sheet

/**
 * Bottom-Sheet. Schließt per Escape und Backdrop-Klick.
 * Kein window.confirm/alert irgendwo in dieser App — alles läuft über Sheets/Toasts.
 */
export function Sheet({
  open,
  onClose,
  title,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  // Der Zurück-Knopf auf Android schließt das Blatt — statt eine Seite zurück zu
  // navigieren und das Blatt über einer fremden Seite stehen zu lassen. In einer
  // nativen App ist das selbstverständlich; ohne diesen Griff ist es das
  // deutlichste "das ist eine Webseite"-Signal, das es gibt.
  useZurueckTaste(open, onClose);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <div
        className="absolute inset-0 animate-fade-in bg-black/70"
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        // Höhe an dem messen, was WIRKLICH sichtbar ist (--app-h), nicht an der
        // Fensterhöhe: Auf iOS legt sich die Tastatur über das Layout, statt es
        // hochzuschieben. Ohne das ragt das Blatt unter die Tastatur.
        style={{ maxHeight: "calc((100dvh - var(--tastatur, 0px)) * 0.92)" }}
        className={cn(
          "relative flex w-full flex-col animate-sheet-up",
          "rounded-t-2xl border-t border-ink-700 bg-ink-900",
          "sm:max-w-lg sm:rounded-2xl sm:border"
        )}
      >
        <header className="flex shrink-0 items-center justify-between border-b border-ink-700/70 px-5 py-4">
          <h2 className="text-[15px] font-semibold text-ink-50">{title}</h2>
          <button
            onClick={onClose}
            className="-mr-2 flex h-9 w-9 items-center justify-center rounded-lg text-ink-400 transition-colors duration-fast hover:bg-ink-800 hover:text-ink-100"
            aria-label="Schließen"
          >
            ✕
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

        {/* Der Knopf muss ÜBER der Tastatur bleiben. Sonst tippt man einen Preis
            ein und kommt nicht mehr an "Speichern" — der häufigste Grund, warum
            Formulare in PWAs unbedienbar wirken. */}
        {footer && (
          <footer
            style={{ paddingBottom: "max(1rem, calc(env(safe-area-inset-bottom) + var(--tastatur, 0px)))" }}
            className="shrink-0 border-t border-ink-700/70 bg-ink-900/95 px-5 py-4"
          >
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}

// -------------------------------------------------------------- EmptyState

export function EmptyState({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
      <p className="text-[15px] font-medium text-ink-300">{title}</p>
      {hint && <p className="max-w-xs text-sm text-ink-500">{hint}</p>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}
